import React, { ReactNode, useMemo } from "react";
import { useTranslation } from "utilities/i18n";
import { State, useTypesafeActions } from "store";
import { TABLE_COLUMN_STATE, TABLES } from "store/userMeta";
import { useSelector } from "react-redux";
import {
  getColumns,
  sortTableData,
  TableBody,
  TableBulkItemHeader,
  TableHeaderItem
} from "collections/Table";
import { SortState } from "store/filters";
import { DeviceInfoSearchEdit } from "./DeviceInfoTableSearch";
import {
  ConnectionStatusIcon,
  DropdownList,
  Loader,
  OptionType,
  ToolTip
} from "elements";
import { generateTableHeaders } from "utilities/tableUtils";
import { ConnectionStatusCell } from "../../List/TableView";
import { handleNullFields } from "utilities/stringUtils";
import { ActionType, createStandardAction, getType } from "typesafe-actions";
import {
  DeviceLinesQueryVariables,
  Omit,
  useDeviceLinesQuery
} from "generated/graphql";
import * as Urql from "polygraphql";
import { noLogoutOnAuthErrorContext } from "polygraphql";

export type DeviceLine = {
  lineId: number;
  status: string;
  registrationType: string;
  domain: string;
  registrationAddress: string;
  uploadTime?: string;
  versionInfo?: string;
  userId?: string;
  sipPort?: string;
  displayName?: string;
  expiry?: string;
  lineType?: string;
  registeredIP?: string;
  srvPlatform?: string;
  eventMonotonicTime?: string;
  sipAddr?: string;
  sipTransport?: string;
  outboundProxyPort?: string;
  outboundProxyTransport?: string;
  failurereason?: string;
};

type DeviceLineFilterState = {
  sort: SortState;
  lineId?: string;
  status?: string;
  registrationType?: string;
  domain?: string;
  registrationAddress?: string;
  searchTerm?: string;
};

type DeviceLineRowProps = DeviceLine & {
  shownColumns: Array<keyof DeviceLine>;
  index: number;
};

export function DeviceLineConnection({
  column,
  status
}: {
  column: string;
  status: string;
}) {
  const { t } = useTranslation();
  const registered = status === "registered";
  const translationValue = registered ? t("REGISTERED") : t("NOT_REGISTERED");
  return (
    <ConnectionStatusCell key={column}>
      <ToolTip
        iconTitle={t("STATUS_INDICATORS")}
        position="bottom"
        size="sm"
        iconReplacement={
          <ConnectionStatusIcon
            connected={registered}
            showDotIcon={true}
            className="m-0"
          />
        }
      >
        <span>{translationValue}</span>
      </ToolTip>
    </ConnectionStatusCell>
  );
}

const DeviceLineRow: React.FC<DeviceLineRowProps> = ({
  shownColumns,
  index,
  ...deviceLine
}) => {
  const { COLUMNS } = TABLES.DEVICE_SERVICE_INFORMATION;

  const getTableContent = (column: keyof DeviceLine, index: number) => {
    let content: ReactNode = null;
    switch (column) {
      case COLUMNS.STATUS:
        return (
          <DeviceLineConnection
            column={column}
            status={deviceLine.status}
            data-testid="status-cell"
            key={column}
          />
        );
      default:
        content = deviceLine[column];
        break;
    }

    return (
      <td key={column} data-testid={`${column}-${index}`}>
        {content}
      </td>
    );
  };

  return <tr>{shownColumns.map(column => getTableContent(column, index))}</tr>;
};

type DeviceLineTableState = {
  filterState: DeviceLineFilterState;
};
const Actions = {
  updateFilterState: createStandardAction("UpdateFilterState")<
    DeviceLineFilterState
  >()
};
type ActionTypes = typeof Actions;
function reducer(
  state: DeviceLineTableState,
  a: ActionType<ActionTypes>
): DeviceLineTableState {
  switch (a.type) {
    case getType(Actions.updateFilterState):
      return {
        ...state,
        filterState: a.payload
      };
  }
}

const ConnectionStatusHeader: React.FC<{
  fieldName: "status";
  filterState: DeviceLineFilterState;
  setFilter: (paramsObj: DeviceLineFilterState) => void;
}> = ({ fieldName, filterState, setFilter }) => {
  const { t } = useTranslation();

  const trueOption = {
    value: "registered",
    text: t("REGISTERED")
  };
  const falseOption = {
    value: "unregistered",
    text: t("NOT_REGISTERED")
  };

  const options: Array<OptionType> = [
    {
      ...trueOption,
      icon: <ConnectionStatusIcon connected={true} className="m-0" />
    },
    {
      ...falseOption,
      icon: <ConnectionStatusIcon connected={false} className="m-0" />
    }
  ];

  if (filterState[fieldName]) {
    options.push({
      value: "",
      text: t("CLEAR_FILTER")
    });
  }

  return (
    <ConnectionStatusCell header>
      <DropdownList
        name="connection"
        className="connection-table-filter"
        placeholder={t("STATUS")}
        data-testid="status-filter"
        options={options}
        onChange={value =>
          setFilter({
            ...filterState,
            status:
              value === "registered"
                ? "registered"
                : value === "unregistered"
                ? "unregistered"
                : undefined
          })
        }
        value={filterState[fieldName]}
        inputReplacement={
          filterState[fieldName] ? (
            <ConnectionStatusIcon
              connected={filterState[fieldName] === trueOption.value}
              className="m-0"
            />
          ) : null
        }
      />
    </ConnectionStatusCell>
  );
};

/**
 * Wraps the "lineQuery" results in a function that
 * processes / maps the data to the desired structure.
 */
export function useLineQueryMapped(
  deviceId: string,
  options?: Omit<
    Urql.UseQueryArgs<DeviceLinesQueryVariables>,
    "query" | "variables"
  >
) {
  const { data, ...rest } = useDeviceLinesQuery({
    variables: {
      deviceId
    },
    context: noLogoutOnAuthErrorContext,
    ...options
  });
  const mapped = useMemo(() => {
    return data?.capabilitiesQuery?.com?.poly?.service_information?.map<
      DeviceLine
    >(
      ({
        regtype,
        linetype,
        lineid,
        status,
        serveraddr,
        sipdomainname,
        expiry,
        userid,
        sipport,
        displayname,
        failurereason,
        outboundproxytransport,
        outboundproxy,
        siptranport,
        sipaddr,
        registeredip,
        srvplatform
      }) => {
        return {
          lineId: lineid?.value ?? -1,
          domain: handleNullFields(sipdomainname?.value ?? "", true),
          registrationAddress: handleNullFields(serveraddr?.value, true),
          registrationType: handleNullFields(regtype?.value, true),
          status:
            status?.value?.toLowerCase() === "registered"
              ? "registered"
              : "unregistered",
          expiry: handleNullFields(expiry?.value?.toString(), true),
          userId: handleNullFields(userid?.value, true),
          sipPort: handleNullFields(sipport?.value?.toString(), true),
          displayName: handleNullFields(displayname?.value, true),
          failurereason: handleNullFields(failurereason?.value, true),
          outboundProxyTransport: handleNullFields(
            outboundproxytransport?.value,
            true
          ),
          outboundProxyPort: handleNullFields(outboundproxy?.value, true),
          sipTransport: handleNullFields(siptranport?.value, true),
          sipAddr: handleNullFields(sipaddr?.value, true),
          registeredIP: handleNullFields(registeredip?.value, true),
          lineType: handleNullFields(linetype?.value, true),
          srvPlatform: handleNullFields(srvplatform?.value, true)
        };
      }
    );
  }, [data?.capabilitiesQuery?.com?.poly?.service_information]);
  return {
    ...rest,
    lines: mapped
  };
}

const defaultFilterState: DeviceLineFilterState = {
  lineId: undefined,
  status: undefined,
  registrationAddress: undefined,
  registrationType: undefined,
  domain: undefined,
  sort: {
    field: "lineId",
    direction: "DESC"
  }
};

export const ServiceInformationTable: React.FC<{ deviceId: string }> = ({
  deviceId
}) => {
  // Fetch the data with a query, once it comes in, store that data in state so we can filter it.
  const { t } = useTranslation();
  const { lines, fetching } = useLineQueryMapped(deviceId);
  const [{ filterState }, { updateFilterState }] = useTypesafeActions(
    reducer,
    {
      filterState: defaultFilterState
    },
    Actions
  );
  const deviceLines = lines ?? [];
  const {
    NAME: TABLE_NAME,
    COLUMNS,
    HIDDEN
  } = TABLES.DEVICE_SERVICE_INFORMATION;
  const {
    [TABLE_COLUMN_STATE.SHOWN]: shownColumns,
    [TABLE_COLUMN_STATE.HIDDEN]: hiddenColumns
  } = useSelector((state: State) => {
    return state.userMeta?.tables[TABLE_NAME] ?? {};
  });

  const columnTranslations = {
    [COLUMNS.LINE_ID]: t("LINE_NUMBER"),
    [COLUMNS.STATUS]: t("STATUS"),
    [COLUMNS.REGISTRATION_TYPE]: t("REGISTRATION_TYPE"),
    [COLUMNS.DOMAIN]: t("DOMAIN"),
    [COLUMNS.REGISTRATION_ADDRESS]: t("SERVER_ADDRESS"),
    [HIDDEN.DISPLAY_NAME]: t("DISPLAY_NAME"),
    [HIDDEN.EXPIRY]: t("EXPIRY"),
    [HIDDEN.FAILURE_REASON]: t("FAILURE_REASON"),
    [HIDDEN.LINE_TYPE]: t("LINE_TYPE"),
    [HIDDEN.OUTBOUND_PROXY_PORT]: t("OUTBOUND_PROXY_PORT"),
    [HIDDEN.OUTBOUND_PROXY_TRANSPORT]: t("OUTBOUND_PROXY_TRANSPORT"),
    [HIDDEN.REGISTERED_IP]: t("REGISTERED_IP"),
    [HIDDEN.SERVER_PLATFORM]: t("SERVER_PLATFORM"),
    [HIDDEN.SIP_ADDRESS]: t("SIP_ADDRESS"),
    [HIDDEN.SIP_PORT]: t("SIP_PORT"),
    [HIDDEN.SIP_TRANSPORT]: t("SIP_TRANSPORT"),
    [HIDDEN.USER_ID]: t("USER_ID")
  };

  let linesToShow: DeviceLine[] = deviceLines
    .filter(line => {
      let unmatchedFilterExists = false;
      const { hasFilter } = Object.entries(filterState).reduce(
        ({ hasFilter }, [key, value]) => {
          const current: unknown = line[key as keyof DeviceLine];
          if (Object.keys(line).includes(key)) {
            if (typeof value === "string" && typeof current === "string") {
              let match = false;
              if (key === "status") {
                match =
                  current.toLocaleLowerCase() === value.toLocaleLowerCase();
              } else {
                match = current
                  .toLocaleLowerCase()
                  .includes(value.toLocaleLowerCase());
              }
              if (!match) unmatchedFilterExists = true;
              return {
                hasFilter: true
              };
            }
            const asN = Number(value);
            if (!isNaN(asN) && typeof current === "number") {
              const match = current === asN;
              if (!match) unmatchedFilterExists = true;
              return {
                hasFilter: true
              };
            }
          }
          return { hasFilter };
        },
        { hasFilter: false }
      );
      return hasFilter ? !unmatchedFilterExists : true;
    })
    .filter(line => {
      const { searchTerm } = filterState;
      if (!searchTerm) {
        return true;
      }
      return Object.values(line).some(x =>
        (x ?? "")
          .toString()
          .toLowerCase()
          .includes(searchTerm.toLowerCase())
      );
    });

  function getValues(field: keyof DeviceLine): string[] {
    return linesToShow.map(line => line[field]?.toString() ?? "");
  }

  // the options on which to filter come from the available data from the lines.
  const filterOptions = {
    [COLUMNS.LINE_ID]: getValues("lineId"),
    [COLUMNS.STATUS]: getValues("status"),
    [COLUMNS.REGISTRATION_TYPE]: getValues("registrationType"),
    [COLUMNS.DOMAIN]: getValues("domain"),
    [COLUMNS.REGISTRATION_ADDRESS]: getValues("registrationAddress"),
    [HIDDEN.DISPLAY_NAME]: getValues("displayName"),
    [HIDDEN.EXPIRY]: getValues("expiry"),
    [HIDDEN.FAILURE_REASON]: getValues("failurereason"),
    [HIDDEN.LINE_TYPE]: getValues("lineType"),
    [HIDDEN.OUTBOUND_PROXY_PORT]: getValues("outboundProxyPort"),
    [HIDDEN.OUTBOUND_PROXY_TRANSPORT]: getValues("outboundProxyTransport"),
    [HIDDEN.REGISTERED_IP]: getValues("registeredIP"),
    [HIDDEN.SERVER_PLATFORM]: getValues("srvPlatform"),
    [HIDDEN.SIP_ADDRESS]: getValues("sipAddr"),
    [HIDDEN.SIP_PORT]: getValues("sipPort"),
    [HIDDEN.SIP_TRANSPORT]: getValues("sipTransport"),
    [HIDDEN.USER_ID]: getValues("userId")
  };
  const shown = getColumns(shownColumns, columnTranslations);
  const hidden = getColumns(hiddenColumns, columnTranslations);

  const getTableHeaders = (column: string, title: string) => {
    switch (column) {
      case COLUMNS.STATUS:
        return (
          <ConnectionStatusHeader
            fieldName="status"
            filterState={filterState}
            setFilter={updateFilterState}
            key={column}
          />
        );
      default:
        const options = Array.from(
          new Set(filterOptions[column].filter(x => x))
        );
        const filter = options
          ? {
              filterValue: (filterState[
                column as keyof DeviceLineFilterState
              ] ?? "") as string,
              filterOptions: options,
              onFilter: (_: string, value: string) => {
                updateFilterState({
                  ...filterState,
                  [column]: value !== "" ? value : undefined
                });
              }
            }
          : undefined;

        const sort = filterState.sort;
        return (
          <TableHeaderItem
            fieldName={column}
            title={title}
            sort={{
              currentSort: sort
                ? {
                    field: sort.field,
                    direction: sort.direction
                  }
                : {
                    field: COLUMNS.LINE_ID,
                    direction: "DESC"
                  },
              onSort: (value: SortState) => {
                updateFilterState({
                  ...filterState,
                  sort: {
                    field: column ?? value.field,
                    direction: value.direction
                  }
                });
              }
            }}
            filter={filter}
            key={column}
          />
        );
    }
  };

  sortTableData(linesToShow, filterState.sort);

  function handleSearch(searchTerm: string | undefined): void {
    updateFilterState({
      ...filterState,
      searchTerm
    });
  }
  return (
    <div className="device-line-table ph-25">
      <section className="grouping table-actions mb-1" role="search">
        <DeviceInfoSearchEdit
          search={{ onSearch: searchTerm => handleSearch(searchTerm) }}
          edit={{
            tableName: TABLE_NAME,
            shownColumns: shown,
            hiddenColumns: hidden
          }}
        />
      </section>
      <Loader loading={fetching}>
        <div className="table-container">
          <table
            className="table custom-table table-filterable"
            data-testid="device-line-table"
          >
            <TableBulkItemHeader
              checked={false}
              showChecks={false}
              onCheck={() => {}}
            >
              {generateTableHeaders(shown, getTableHeaders)}
            </TableBulkItemHeader>
            <TableBody loading={false} cols={5}>
              {linesToShow.map((line, i) => (
                <DeviceLineRow
                  key={line.lineId + line.registrationType}
                  index={i}
                  shownColumns={shown.map(
                    column => column.key as keyof DeviceLine
                  )}
                  {...line}
                />
              ))}
            </TableBody>
          </table>
        </div>
      </Loader>
    </div>
  );
};
